import { useCallback, useEffect, useRef, useState } from 'react'

import { ApiRequestError } from '../../api.ts'

export type SectionQuery<T> = {
  data: T | null
  error: string | null
  isLoading: boolean
  retry: () => void
}

const errorMessage = (error: unknown, sectionLabel: string): string => {
  if (error instanceof ApiRequestError && error.body.error.message) return error.body.error.message
  if (error instanceof Error && error.message) return error.message
  return `${sectionLabel} unavailable`
}

export const useSectionQuery = <T>(load: () => Promise<T>, sectionLabel: string): SectionQuery<T> => {
  const [data, setData] = useState<T | null>(null)
  const [error, setError] = useState<string | null>(null)
  const [isLoading, setIsLoading] = useState(true)
  const loadRef = useRef(load)
  const sectionLabelRef = useRef(sectionLabel)
  const requestId = useRef(0)

  useEffect(() => {
    loadRef.current = load
    sectionLabelRef.current = sectionLabel
  }, [load, sectionLabel])

  const run = useCallback((): void => {
    const currentRequestId = ++requestId.current
    setIsLoading(true)
    setError(null)

    let request: Promise<T>
    try {
      request = loadRef.current()
    } catch (requestError) {
      if (requestId.current !== currentRequestId) return
      setError(errorMessage(requestError, sectionLabelRef.current))
      setIsLoading(false)
      return
    }

    void request
      .then((nextData) => {
        if (requestId.current !== currentRequestId) return
        setData(nextData)
        setIsLoading(false)
      })
      .catch((requestError: unknown) => {
        if (requestId.current !== currentRequestId) return
        setError(errorMessage(requestError, sectionLabelRef.current))
        setIsLoading(false)
      })
  }, [])

  useEffect(() => {
    run()

    return () => {
      requestId.current += 1
    }
  }, [run])

  return { data, error, isLoading, retry: run }
}
